import React, { useState } from "react";
import {
  Check,
  HelpCircle,
  Sparkles,
  X,
  ChevronDown,
  ShieldCheck,
  Layers,
  Award,
  ArrowRight,
} from "lucide-react";
import { transform } from "lodash";

const Pricing = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const plans = [
    {
      name: "Starter",
      description:
        "Ideal for individual placement cells launching initial drives.",
      badge: "Institutional Basic",
      basePrice: 15000,
      discountedBasePrice: null,
      gstRate: 0.18,
      gstAmount: 2700,
      offerPrice: 17700,
      studentsLimit: "Up to 100 Students",
      popular: false,
      cta: "Activate Starter Roster",
      features: [
        "Up to 100 active student verification",
        "300+ Verified HR Leads / Month",
        "Connect HR to Schedule Off campus Drive",
        "Track Student Progress and Application Matrix",
        "Centralized Placement Tracking Dashboard",
        "Communication Hooks (Email/Whatsapp)",
      ],
      notIncluded: [
        "ATS Resume Builder Live Session",
        "AI Co Pilot - Interview Prepare from JD",
        "Priority Support",
      ],
    },
    {
      name: "Premium Silver",
      description: "Best for growing engineering and business departments.",
      badge: "Best Selling Choice",
      basePrice: 40000,
      discountedBasePrice: 32000,
      gstRate: 0.18,
      gstAmount: 5760,
      offerPrice: 37760,
      studentsLimit: "Up to 500 Students",
      popular: true,
      cta: "Go Premium Silver",
      features: [
        "Up to 500 active student verification",
        "300+ Verified HR Leads / Month",
        "Connect HR to Schedule Off campus Drive",
        "Track Student Progress and Application Matrix",
        "Centralized Placement Tracking Dashboard",
        "ATS Resume Builder Live Session",
        "AI Co Pilot - Interview Prepare from JD",
        "Priority Support",
        "Communication Hooks (Email/Whatsapp)",
      ],
      notIncluded: [],
    },
    {
      name: "Premium Gold",
      description:
        "Engineered for comprehensive campus-wide placement operations.",
      badge: "Enterprise Standard",
      basePrice: 70000,
      discountedBasePrice: 56000,
      gstRate: 0.18,
      gstAmount: 10080,
      offerPrice: 66080,
      studentsLimit: "500–1000 Students",
      popular: false,
      cta: "Deploy Premium Gold",
      features: [
        "Up to 1,000 active student verification",
        "300+ Verified HR Leads / Month",
        "Connect HR to Schedule Off campus Drive",
        "Track Student Progress and Application Matrix",
        "Centralized Placement Tracking Dashboard",
        "ATS Resume Builder Live Session",
        "AI Co Pilot - Interview Prepare from JD",
        "Priority Support",
        "Communication Hooks (Email/Whatsapp)",
      ],
      notIncluded: [],
    },
  ];

  const comparisonMatrix = [
    {
      category: "ROSTER & PLACEMENT MANAGEMENT",
      features: [
        {
          name: "Active Student Verification Limit",
          starter: "Up to 100 Students",
          silver: "Up to 500 Students",
          gold: "Up to 1,000 Students",
        },
        {
          name: "Verified HR Leads / Month",
          starter: "300+",
          silver: "300+",
          gold: "300+",
        },
        {
          name: "Connect HR to Schedule Off campus Drive",
          starter: "✅ Included",
          silver: "✅ Included",
          gold: "✅ Included",
        },
        {
          name: "Track Student Progress & Application Matrix",
          starter: "✅ Included",
          silver: "✅ Included",
          gold: "✅ Included",
        },
        {
          name: "Centralized Placement Tracking Dashboard",
          starter: "✅ Included",
          silver: "✅ Included",
          gold: "✅ Included",
        },
        {
          name: "Communication Hooks (Email/Whatsapp)",
          starter: "✅ Included",
          silver: "✅ Included",
          gold: "✅ Included",
        },
      ],
    },
    {
      category: "ADVANCED SERVICES & SUPPORT",
      features: [
        {
          name: "ATS Resume Builder Live Session",
          starter: "❌ Not Available",
          silver: "✅ Included",
          gold: "✅ Included",
        },
        {
          name: "AI Co Pilot - Interview Prepare from JD",
          starter: "❌ Not Available",
          silver: "✅ Included",
          gold: "✅ Included",
        },
        {
          name: "Priority Support",
          starter: "❌ Not Available",
          silver: "✅ Included",
          gold: "✅ Included",
        },
      ],
    },
  ];

  const faqs = [
    {
      q: "What is the difference between the Institutional Basic and Premium Silver (Best Selling Choice) plans?",
      a: "The Institutional Basic plan is designed for individual placement cells launching initial drives, allowing management of up to 100 students. The Premium Silver (Best Selling Choice) plan supports up to 500 students and adds premium placement acceleration tools including the ATS Resume Builder Live Session, AI Co-Pilot interview preparations directly mapped from JDs, and priority 24/7 technical support.",
    },
    {
      q: "Does the 18% GST apply to all EduConnectPro subscription rates?",
      a: "Yes. All subscription tiers are 100% compliant with standard Indian GST taxation rules (18% TAX). Our institutional invoices provide explicit breakdowns of CGST (9%) and SGST (9%) or IGST (18%) to ensure seamless tax filing and allow your institution's accounts department to claim Input Tax Credit (ITC) efficiently.",
    },
    {
      q: "How does the student verification capacity limit work for each plan?",
      a: "The capacity represents the maximum number of verified student profiles that can actively maintain academic roster locks and participate in campaign broadcaster dispatches inside your college directory. The Institutional Basic tier supports Up to 100 Students, the Best Selling Choice tier supports Up to 500 Students, and our Premium Gold tier supports 500–1,000 Students. You can scale your capacity limit seamlessly mid-year if your intake increases.",
    },
    {
      q: "What features are included in the AI-Enabled package of EduConnectPro?",
      a: "The AI-Enabled packages (Premium Silver and Premium Gold) feature an advanced LLM-powered resume parsing and analysis engine. This includes the 'AI Co-Pilot - Interview Prepare from JD' tool which dynamically constructs relevant mock questionnaires, filters candidates by match criteria, and hosts automatic ATS Resume Builder Live Sessions to guarantee a polished student roster.",
    },
    {
      q: "Is recruiter portal access free under these institutional pricing plans?",
      a: "Yes, absolutely! Inviting HR representatives, scheduling on-campus or off-campus recruitment drives, running custom eligibility query filters, and sharing locked student rosters is completely free across all subscription plans (Institutional Basic, Premium Silver, and Premium Gold).",
    },
    {
      q: "How does the Academic Roster Lock feature prevent roster tampering?",
      a: "Once student academic parameters (such as CGPA, outstanding backlogs, and branch eligibility keys) are uploaded and verified by your TPO command center, they are fully locked. Students are strictly prohibited from modifying these verified figures on their apps, preventing false profile generation and protecting your university's reputation with top corporate partners.",
    },
    {
      q: "Can we generate institutional placement audit reports for NAAC or NIRF?",
      a: "Yes. All plans include a robust one-click visual reporting engine. You can instantly export audited spreadsheets and summaries detailing verified visual statistics, average salary packages (LPA metrics), sector-wise selection trends, and historical placement milestones required for regulatory inspections.",
    },
    {
      q: "How do we cancel our annual subscription, and are refunds supported?",
      a: "Subscriptions are billed annually. You can turn off auto-renewal at any time from your institutional dashboard. While we do not offer pro-rated mid-term refunds, your student roster locks, campaign broadcaster, and secure port access remain active until the end of your prepaid annual billing period.",
    },
  ];

  return (
    <div className="position-relative  overflow-hidden" style={styles.page}>
      {/* Premium background grid patterns */}
      <div style={styles.gridBg} />

      {/* Decorative premium ambient lights */}
      <div style={styles.ambientLight1} />
      <div style={styles.ambientLight2} />
      <div style={styles.ambientLight3} />

      {/* Header */}
      <div
        className="text-center mx-auto position-relative"
        style={styles.headerWrapper}
      >
        <span
          className="d-inline-flex align-items-center badgeLabel font-display"
          style={styles.badgeLabel}
        >
          <Sparkles style={{ width: "14px", height: "14px" }} />
          EduConnectPro Premium Membership — AI Enabled
        </span>
        <h1 style={styles.title} className="font-display">
          Transparent Annual Pricing
        </h1>
        <p className="subtitle" style={styles.subtitle}>
          Continuous placement automation tools, eligibility checking arrays,
          and real-time candidate locks for Indian colleges.
        </p>

        <div
          className="d-inline-flex align-items-center"
          style={styles.gstBadge}
        >
          <ShieldCheck
            style={{
              width: "18px",
              height: "18px",
              color: "#FF5F14",
              flexShrink: 0,
            }}
          />
          <span className="font-display badgeLabel">
            GST 18% TAX COMPLIANT INSTITUTIONAL RATES
          </span>
        </div>
      </div>

      {/* Cards Grid */}
      <div
        className="mx-auto container position-relative"
        style={styles.cardsWrapper}
      >
        <div className="row g-4 justify-content-center">
          {plans.map((plan, idx) => {
            const isPro = plan.popular;
            return (
              <div key={idx} className="col-12 col-md-6 col-lg-4">
                <div
                  className={`d-flex flex-column justify-content-between position-relative h-100 bg-white ${isPro ? "" : "cardNormal"}`}
                  style={isPro ? styles.cardPro : styles.cardNormal}
                >
                  {isPro && (
                    <div
                      className="font-display position-absolute  popularBadge d-flex align-items-center justify-content-center"
                      style={styles.popularBadge}
                    >
                      <Award
                        style={{
                          width: "14px",
                          height: "14px",
                          marginRight: "4px",
                        }}
                      />
                      POPULAR SELECTION
                    </div>
                  )}

                  <div>
                    {/* Badge header */}
                    <div className="d-flex font-display justify-content-between align-items-center mb-4">
                      <span style={styles.planBadge}>{plan.badge}</span>
                      <span style={styles.planLimit}>{plan.studentsLimit}</span>
                    </div>

                    <h3 className="font-display" style={styles.planName}>
                      {plan.name}
                    </h3>
                    <p style={styles.planDesc}>{plan.description}</p>

                    {/* Beautiful dynamic pricing engine block */}
                    <div
                      className="position-relative overflow-hidden"
                      style={styles.priceBlock}
                    >
                      <div style={styles.priceBlockCorner} />

                      <div className="d-flex justify-content-between align-items-baseline mb-2">
                        <span
                          className="font-display"
                          style={styles.priceLabel}
                        >
                          Base Price (Annual):
                        </span>
                        <div
                          className="d-flex   align-items-center"
                          style={{ gap: "6px" }}
                        >
                          {plan.discountedBasePrice ? (
                            <>
                              <span
                                className="font-display"
                                style={styles.strikethroughPrice}
                              >
                                ₹{plan.basePrice.toLocaleString()}
                              </span>
                              <span
                                className="font-display"
                                style={styles.basePrice}
                              >
                                ₹{plan.discountedBasePrice.toLocaleString()}
                              </span>
                            </>
                          ) : (
                            <span
                              className="font-display"
                              style={styles.basePrice}
                            >
                              ₹{plan.basePrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        className="d-flex justify-content-between align-items-baseline"
                        style={styles.gstRow}
                      >
                        <span className="font-display" style={styles.gstLabel}>
                          GST (18%):
                        </span>
                        <span className="font-display" style={styles.gstValue}>
                          ₹{plan.gstAmount.toLocaleString()}
                        </span>
                      </div>

                      <div
                        className="font-display d-flex justify-content-between align-items-baseline"
                        style={styles.offerRow}
                      >
                        <span
                          className="font-display"
                          style={styles.offerLabel}
                        >
                          OFFER PRICE (NET):
                        </span>
                        <div
                          className="d-flex align-items-baseline"
                          style={{ gap: "2px" }}
                        >
                          <span
                            className="font-display"
                            style={styles.currencySymbol}
                          >
                            ₹
                          </span>
                          <span
                            className="font-display"
                            style={styles.offerPrice}
                          >
                            {plan.offerPrice.toLocaleString()}
                          </span>
                          <span className="font-display" style={styles.perYear}>
                            / yr
                          </span>
                        </div>
                      </div>
                    </div>

                    <div style={styles.divider} />

                    {/* Features included */}
                    <div style={styles.featuresList}>
                      {plan.features.map((feat, fIdx) => (
                        <div
                          key={fIdx}
                          className="d-flex align-items-start"
                          style={{ gap: "12px", marginBottom: "14px" }}
                        >
                          <div style={styles.checkIconWrapper}>
                            <Check style={styles.checkIcon} />
                          </div>
                          <span style={styles.featureText}>{feat}</span>
                        </div>
                      ))}
                      {plan.notIncluded.map((feat, fIdx) => (
                        <div
                          key={fIdx}
                          className="d-flex align-items-start"
                          style={{
                            gap: "12px",
                            marginBottom: "14px",
                            opacity: 0.4,
                          }}
                        >
                          <div style={styles.crossIconWrapper}>
                            <X style={styles.crossIcon} />
                          </div>
                          <span style={styles.featureTextDisabled}>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  <a
                    href="#/about-edu"
                    className={`${isPro ? "orangeBtn" : ""}`}
                    style={isPro ? styles.ctaPro : styles.ctaNormal}
                    onMouseOver={(e) => {
                      if (isPro)
                        e.currentTarget.style.backgroundColor = "#e04f0f";
                      else e.currentTarget.style.backgroundColor = "#020617";
                    }}
                    onMouseOut={(e) => {
                      if (isPro)
                        e.currentTarget.style.backgroundColor = "#FF5F14";
                      else e.currentTarget.style.backgroundColor = "#0f172a";
                    }}
                  >
                    <span className="font-display">{plan.cta}</span>
                    <ArrowRight style={{ width: "14px", height: "14px" }} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Comparison Matrix Table */}
      <div className="mx-auto bg-white" style={styles.matrixWrapper}>
        <div className="position-relative" style={styles.matrixHeader}>
          <div style={styles.matrixHeaderLine} />
          <div
            className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center"
            style={{ gap: "1rem" }}
          >
            <div className="d-flex align-items-center" style={{ gap: "12px" }}>
              <div style={styles.matrixIconBox}>
                <Layers
                  style={{ width: "20px", height: "20px", color: "#FF5F14" }}
                />
              </div>
              <div>
                <h3
                  className="font-display matrixTitle "
                  style={styles.matrixTitle}
                >
                  EduConnectPro Feature Matrix
                </h3>
                <p style={styles.matrixSubtitle}>
                  Comprehensive comparison of system boundaries and operational
                  limits.
                </p>
              </div>
            </div>
            <span className="font-display" style={styles.matrixBadge}>
              ANNUAL COMPLIANT PACKAGES
            </span>
          </div>
        </div>

        <div className="table-responsive">
          <table
            className="table mb-0 text-start border-collapse"
            style={{ minWidth: "760px" }}
          >
            <thead>
              <tr style={styles.tableHeadRow}>
                <th
                  className="font-display"
                  style={{ ...styles.th, width: "40%", paddingLeft: "2rem" }}
                >
                  Feature / Platform Parameter
                </th>
                <th
                  className="font-display"
                  style={{ ...styles.th, color: "#475569" }}
                >
                  Starter
                </th>
                <th
                  className="font-display"
                  style={{
                    ...styles.th,
                    color: "#FF5F14",
                    backgroundColor: "rgba(255, 237, 213, 0.3)",
                  }}
                >
                  Premium Silver
                </th>
                <th
                  className="font-display"
                  style={{ ...styles.th, color: "#0f172a" }}
                >
                  Premium Gold
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonMatrix.map((cat, catIdx) => (
                <React.Fragment key={catIdx}>
                  {/* Category Header */}
                  <tr style={styles.categoryRow}>
                    <td
                      colSpan={4}
                      className="font-display"
                      style={{
                        padding: "1rem 2rem",
                        textTransform: "uppercase",
                      }}
                    >
                      {cat.category}
                    </td>
                  </tr>

                  {cat.features.map((feat, featIdx) => {
                    const renderCell = (val, isSilver = false) => {
                      if (val.includes("✅")) {
                        return (
                          <div
                            className="d-inline-flex align-items-center"
                            style={styles.cellIncluded}
                          >
                            <div style={styles.cellCheckWrapper}>
                              <Check
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  color: "#059669",
                                  strokeWidth: 3.5,
                                }}
                              />
                            </div>
                            <span>Included</span>
                          </div>
                        );
                      }
                      if (val.includes("❌")) {
                        return (
                          <div
                            className="d-inline-flex align-items-center"
                            style={styles.cellNotIncluded}
                          >
                            <div style={styles.cellCrossWrapper}>
                              <X
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  color: "#94a3b8",
                                  strokeWidth: 3,
                                }}
                              />
                            </div>
                            <span>Not Available</span>
                          </div>
                        );
                      }
                      return (
                        <span
                          className="font-display"
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: 900,
                            letterSpacing: "-0.025em",
                            color: isSilver ? "#FF5F14" : "#1e293b",
                          }}
                        >
                          {val}
                        </span>
                      );
                    };

                    return (
                      <tr
                        key={featIdx}
                        style={styles.featureRow}
                        className="align-middle"
                      >
                        <td
                          style={{
                            ...styles.td,
                            paddingLeft: "2rem",
                            fontWeight: 700,
                            color: "#1e293b",
                            fontSize: "0.875rem",
                          }}
                        >
                          {feat.name}
                        </td>
                        <td style={styles.td}>{renderCell(feat.starter)}</td>
                        <td
                          style={{
                            ...styles.td,
                            backgroundColor: "rgba(255, 237, 213, 0.1)",
                          }}
                        >
                          {renderCell(feat.silver, true)}
                        </td>
                        <td style={styles.td}>{renderCell(feat.gold)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing specific FAQ - Accordion section */}
      <div className="mx-auto  position-relative" style={styles.faqWrapper}>
        <div className="text-center" style={{ marginBottom: "4rem" }}>
          <span
            className="d-inline-flex font-display align-items-center"
            style={styles.faqBadge}
          >
            <HelpCircle
              style={{ width: "14px", height: "14px", color: "#FF5F14" }}
            />
            HELP & CONTRACT GUIDELINES
          </span>
          <p className="font-display faqTitle" style={styles.faqTitle}>
            EduConnectPro Operations FAQ
          </p>
          <p style={styles.faqSubtitle}>
            Everything you need to know about billing, tax compliance, and
            database operations.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {faqs.map((faq, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={idx}
                style={isOpen ? styles.faqCardOpen : styles.faqCardClosed}
              >
                <button
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  style={styles.faqButton}
                >
                  <span style={styles.faqQuestion}>{faq.q}</span>
                  <div
                    style={
                      isOpen
                        ? styles.faqIconWrapperOpen
                        : styles.faqIconWrapperClosed
                    }
                  >
                    <ChevronDown
                      style={{
                        width: "16px",
                        height: "16px",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  </div>
                </button>
                {isOpen && (
                  <div className="font-sans" style={styles.faqAnswer}>
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <style>{`
      .cardNormal:hover{
        border-color:oklch(86.9% 0.022 252.894) !important;
      }
        .subtitle{font-size:1rem;}
        .faqTitle{font-size:2.25rem;}
        .matrixTitle{font-size: 1.25rem;margin:0;}
        .table-responsive {overflow-x: auto;}

        @media (max-width:1064px)
        {
          .popularBadge{font-size: 8.5px !important;}
        }
         @media (max-width:  540px)
        {
          .faqTitle{font-size: 23px;}
        }
        @media (max-width:  480px)
        {
          .subtitle{font-size:11px;}
          .matrixTitle{font-size:15px;}
          .badgeLabel{font-size:0.65rem !important;}

        }
       
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    color: "#0f172a",
    padding: "6rem 1rem",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  gridBg: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(to right, rgba(128,128,128,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(128,128,128,0.04) 1px, transparent 1px)",
    backgroundSize: "14px 24px",
    maskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    WebkitMaskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    pointerEvents: "none",
  },
  ambientLight1: {
    position: "absolute",
    top: "3rem",
    left: "25%",
    width: "500px",
    height: "500px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.05)",
    filter: "blur(130px)",
    pointerEvents: "none",
  },
  ambientLight2: {
    position: "absolute",
    top: "40%",
    right: "25%",
    width: "600px",
    height: "600px",
    borderRadius: "50%",
    backgroundColor: "rgba(59, 130, 246, 0.03)",
    filter: "blur(160px)",
    pointerEvents: "none",
  },
  ambientLight3: {
    position: "absolute",
    bottom: "3rem",
    left: "2.5rem",
    width: "450px",
    height: "450px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 237, 213, 0.2)",
    filter: "blur(140px)",
    pointerEvents: "none",
  },
  headerWrapper: {
    maxWidth: "56rem",
    marginBottom: "5rem",
    zIndex: 10,
  },
  badgeLabel: {
    gap: "0.5rem",
    padding: "0.5rem 1rem",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: "1rem",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  title: {
    fontSize: "2.25rem",
    fontWeight: 900,
    color: "#020617",
    letterSpacing: "-0.025em",
    lineHeight: 1,
    marginBottom: "1rem",
  },
  subtitle: {
    color: "#64748b",
    fontWeight: 600,
    lineHeight: 1.625,
    maxWidth: "42rem",
    margin: "0 auto 2rem auto",
  },
  gstBadge: {
    gap: "0.625rem",
    padding: "0.75rem 1.25rem",
    borderRadius: "0.75rem",
    backgroundColor: "rgba(249, 115, 22, 0.1)",
    border: "1px solid rgba(254, 215, 170, 0.6)",
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    boxShadow: "0 4px 15px rgba(255,95,20,0.04)",
  },
  cardsWrapper: {
    // maxWidth: "80rem",
    marginBottom: "7rem",
    zIndex: 10,
  },
  cardNormal: {
    padding: "2rem",
    borderRadius: "1.5rem",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    boxShadow: "0 20px 25px -5px rgba(241, 245, 249, 0.4)",
    transition: "all 0.3s ease",
  },
  cardPro: {
    padding: "2rem",
    borderRadius: "1.5rem",
    border: "2px solid #FF5F14",
    boxShadow:
      "0 25px 50px -12px rgba(255, 95, 20, 0.08), 0 0 0 4px rgba(249, 115, 22, 0.05)",
    transition: "all 0.3s ease",
  },
  popularBadge: {
    top: "-14px",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "4px 16px",
    borderRadius: "9999px",
    backgroundColor: "#FF5F14",
    color: "#fff",
    fontSize: "9.5px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    boxShadow: "0 10px 15px -3px rgba(255, 95, 20, 0.25)",
  },
  planBadge: {
    fontSize: "10.5px",
    fontWeight: 900,
    color: "#FF5F14",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    backgroundColor: "#fff7ed",
    padding: "4px 10px",
    borderRadius: "6px",
  },
  planLimit: {
    fontSize: "11px",
    fontWeight: 900,
    backgroundColor: "#f1f5f9",
    color: "#1e293b",
    padding: "4px 12px",
    borderRadius: "8px",
  },
  planName: {
    fontSize: "1.5rem",
    fontWeight: 900,
    color: "#0f172a",
    letterSpacing: "-0.025em",
    marginTop: "0.5rem",
  },
  planDesc: {
    fontSize: "0.75rem",
    color: "#64748b",
    marginTop: "0.5rem",
    lineHeight: 1.625,
    minHeight: "2.5rem",
    fontWeight: 600,
  },
  priceBlock: {
    margin: "1.5rem 0",
    padding: "1.25rem",
    borderRadius: "1rem",
    backgroundColor: "rgba(248, 250, 252, 0.7)",
    border: "1px solid rgba(226, 232, 240, 0.6)",
  },
  priceBlockCorner: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "4rem",
    height: "4rem",
    background:
      "linear-gradient(to bottom right, rgba(255, 95, 20, 0.05), transparent)",
    borderBottomLeftRadius: "9999px",
    pointerEvents: "none",
  },
  priceLabel: {
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#94a3b8",
  },
  strikethroughPrice: {
    fontSize: "0.75rem",
    color: "#94a3b8",
    fontWeight: 700,
    textDecoration: "line-through",
  },
  basePrice: {
    fontSize: "1rem",
    fontWeight: 900,
    color: "#0f172a",
  },
  gstRow: {
    borderTop: "1px solid rgba(226, 232, 240, 0.5)",
    paddingTop: "0.5rem",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: 600,
  },
  gstLabel: {
    color: "#94a3b8",
  },
  gstValue: {
    fontWeight: 700,
    color: "#334155",
  },
  offerRow: {
    borderTop: "1px solid rgba(226, 232, 240, 0.5)",
    paddingTop: "0.75rem",
    marginTop: "0.5rem",
  },
  offerLabel: {
    fontSize: "10px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#FF5F14",
  },
  currencySymbol: {
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: 900,
  },
  offerPrice: {
    fontSize: "1.875rem",
    fontWeight: 900,
    color: "#FF5F14",
    letterSpacing: "-0.025em",
  },
  perYear: {
    fontSize: "10px",
    color: "#94a3b8",
    fontWeight: 900,
    marginLeft: "2px",
  },
  divider: {
    borderTop: "1px solid #f1f5f9",
    margin: "1.25rem 0",
  },
  featuresList: {
    marginBottom: "2rem",
  },
  checkIconWrapper: {
    backgroundColor: "#ecfdf5",
    borderRadius: "50%",
    padding: "2px",
    marginTop: "2px",
  },
  checkIcon: {
    width: "16px",
    height: "16px",
    color: "#059669",
  },
  featureText: {
    fontSize: "0.75rem",
    color: "#475569",
    fontWeight: 700,
    lineHeight: 1.625,
  },
  crossIconWrapper: {
    backgroundColor: "#f1f5f9",
    borderRadius: "50%",
    padding: "2px",
    marginTop: "2px",
  },
  crossIcon: {
    width: "16px",
    height: "16px",
    color: "#94a3b8",
  },
  featureTextDisabled: {
    fontSize: "0.75rem",
    color: "#94a3b8",
    fontWeight: 700,
    lineHeight: 1.625,
    textDecoration: "line-through",
  },
  ctaPro: {
    width: "100%",
    padding: "0.875rem 1rem",
    borderRadius: "0.75rem",
    fontWeight: 900,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    backgroundColor: "#FF5F14",
    color: "#fff",
    textDecoration: "none",
    boxShadow: "0 10px 15px -3px rgba(255, 95, 20, 0.2)",
    transition: "all 0.3s ease",
    border: "none",
    cursor: "pointer",
  },
  ctaNormal: {
    width: "100%",
    padding: "0.875rem 1rem",
    borderRadius: "0.75rem",
    fontWeight: 900,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    backgroundColor: "#0f172a",
    color: "#fff",
    textDecoration: "none",
    boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.1)",
    transition: "all 0.3s ease",
    border: "none",
    cursor: "pointer",
  },
  matrixWrapper: {
    maxWidth: "64rem",
    marginBottom: "7rem",
    borderRadius: "1.5rem",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    boxShadow: "0 25px 50px -12px rgba(241, 245, 249, 0.4)",
    zIndex: 10,
    overflow: "hidden",
    position: "relative",
  },
  matrixHeader: {
    padding: "1.5rem 2rem",
    borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
    backgroundColor: "rgba(248, 250, 252, 0.4)",
  },
  matrixHeaderLine: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(to right, #FF5F14, #fb923c)",
  },
  matrixIconBox: {
    padding: "10px",
    borderRadius: "1rem",
    backgroundColor: "#fff7ed",
    border: "1px solid rgba(255, 237, 213, 0.5)",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  matrixTitle: {
    fontWeight: 900,
    color: "#020617",
    letterSpacing: "-0.025em",
  },
  matrixSubtitle: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: 600,
    margin: "0",
  },
  matrixBadge: {
    fontSize: "10.5px",
    fontWeight: 900,
    letterSpacing: "0.05em",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    color: "#FF5F14",
    padding: "6px 16px",
    borderRadius: "9999px",
  },
  tableHeadRow: {
    backgroundColor: "rgba(248, 250, 252, 0.6)",
    fontSize: "10.5px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
  },
  th: {
    padding: "1.25rem",
    borderBottom: "none",
    fontSize: "13px",
    textTransform: "uppercase",
  },
  categoryRow: {
    backgroundColor: "rgba(248, 250, 252, 0.3)",
    borderTop: "1px solid rgba(226, 232, 240, 0.4)",
    borderBottom: "1px solid rgba(226, 232, 240, 0.4)",
    fontSize: "10px",
    fontWeight: 900,
    letterSpacing: "0.05em",
    color: "#94a3b8",
  },
  featureRow: {
    borderBottom: "1px solid #f1f5f9",
  },
  td: {
    padding: "1.25rem",
    borderBottom: "none",
    verticalAlign: "middle",
  },
  cellIncluded: {
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "9999px",
    backgroundColor: "#ecfdf5",
    border: "1px solid rgba(209, 250, 229, 0.6)",
    color: "#047857",
    fontSize: "11px",
    fontWeight: 800,
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  cellCheckWrapper: {
    backgroundColor: "#fff",
    borderRadius: "50%",
    padding: "2px",
  },
  cellNotIncluded: {
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "9999px",
    backgroundColor: "#f8fafc",
    border: "1px solid rgba(226, 232, 240, 0.5)",
    color: "#94a3b8",
    fontSize: "11px",
    fontWeight: 700,
  },
  cellCrossWrapper: {
    backgroundColor: "#fff",
    borderRadius: "50%",
    padding: "2px",
  },
  faqWrapper: {
    maxWidth: "56rem",
    borderTop: "1px solid rgba(226, 232, 240, 0.8)",
    paddingTop: "6rem",
    zIndex: 10,
  },
  faqBadge: {
    gap: "6px",
    padding: "6px 14px",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    color: "#FF5F14",
    fontSize: "10px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: "0.75rem",
  },
  faqTitle: {
    fontWeight: 900,
    color: "#020617",
    letterSpacing: "-0.025em",
  },
  faqSubtitle: {
    fontSize: "0.875rem",
    color: "#64748b",
    fontWeight: 500,
    marginTop: "0.5rem",
  },
  faqCardClosed: {
    borderRadius: "1rem",
    border: "1px solid #e2e8f0",
    backgroundColor: "#fff",
    overflow: "hidden",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
  },
  faqCardOpen: {
    borderRadius: "1rem",
    border: "1px solid #FF5F14",
    background: "linear-gradient(to bottom, #fff, rgba(255, 237, 213, 0.2))",
    overflow: "hidden",
    boxShadow: "0 20px 25px -5px rgba(255, 95, 20, 0.05)",
    transition: "all 0.3s ease",
  },
  faqButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "1.25rem",
    textAlign: "left",
    cursor: "pointer",
    background: "none",
    border: "none",
  },
  faqQuestion: {
    fontSize: "0.975rem",
    fontWeight: 800,
    color: "#0f172a",
    paddingRight: "1rem",
  },
  faqIconWrapperClosed: {
    padding: "0.5rem",
    borderRadius: "0.75rem",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    flexShrink: 0,
  },
  faqIconWrapperOpen: {
    padding: "0.3rem 0.5rem 0.4rem",
    borderRadius: "0.75rem",
    border: "1px solid #FF5F14",
    backgroundColor: "#fff7ed",
    color: "#FF5F14",
    flexShrink: 0,
  },
  faqAnswer: {
    padding: "0 1.25rem 1.5rem 1.25rem",
    fontSize: "0.875rem",
    color: "#475569",
    lineHeight: 1.625,
    borderTop: "1px solid #f1f5f9",
    paddingTop: "1.25rem",
    fontWeight: 600,
    backgroundColor: "rgba(255,255,255,0.4)",
  },
};

export default Pricing;
